import { compile as baseCompile, parse as baseParse } from "path-to-regexp";

const LEGACY_OPTIONAL_PARAM_PATTERN = /\/:([A-Za-z0-9_]+)\?/g;

/**
 * Convert legacy `path-to-regexp` v1/v2 syntax into the v8 syntax.
 * Keeps the route strings compatible with React Router while letting us
 * generate helpers using the latest `path-to-regexp` implementation.
 */
export const convertLegacyPathPattern = (pattern: string): string =>
	pattern.replace(LEGACY_OPTIONAL_PARAM_PATTERN, "{/:$1}");

const splitPattern = (pattern: string) => {
	const isIdentifierChar = (char: string) => /[A-Za-z0-9_]/.test(char);

	let queryIndex = -1;
	for (let i = 0; i < pattern.length; i++) {
		if (pattern[i] !== "?") {
			continue;
		}

		let cursor = i - 1;
		while (cursor >= 0 && isIdentifierChar(pattern[cursor])) {
			cursor--;
		}

		if (pattern[cursor] === ":") {
			continue;
		}

		queryIndex = i;
		break;
	}

	if (queryIndex === -1) {
		return { path: pattern, suffix: "" };
	}

	return {
		path: pattern.slice(0, queryIndex),
		suffix: pattern.slice(queryIndex),
	};
};

export const compilePathPattern: typeof baseCompile = (pattern, options) => {
	if (typeof pattern !== "string") {
		return baseCompile(pattern, options);
	}

	const { path, suffix } = splitPattern(pattern);
	const compiled = baseCompile(convertLegacyPathPattern(path), options);

	const normalizeParams = (params: Record<string, unknown> | undefined) => {
		if (!params) {
			return params;
		}

		const normalised: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(params)) {
			if (Array.isArray(value)) {
				normalised[key] = value.map(item => (item === null || item === undefined ? item : String(item)));
			} else if (value === null || value === undefined || typeof value === "string") {
				normalised[key] = value;
			} else {
				normalised[key] = String(value);
			}
		}

		return normalised as Record<string, string | string[]>;
	};

	if (!suffix) {
		return params => compiled(normalizeParams(params as Record<string, unknown> | undefined));
	}

	return params => `${compiled(normalizeParams(params as Record<string, unknown> | undefined))}${suffix}`;
};

export const parsePathPattern: typeof baseParse = (pattern, options) =>
	baseParse(convertLegacyPathPattern(splitPattern(pattern).path), options);
